import { useEffect, useState } from 'react';
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { apiAction } from '../lib/api';
import ThemeToggle from './ThemeToggle';
import ScoreboardRibbon from './ScoreboardRibbon';
import { RAIL_SECTIONS, RAIL_TAIL, canSeeNav, visibleLeaves } from '../lib/rail-nav';
import { personaOpenJobs, personaLabel, matchPersona } from '../lib/personas';
import ImpersonationBanner from './ImpersonationBanner';
import UpdateBanner from './UpdateBanner';
import type { AuthMe } from '../lib/auth-types';
import { setViewAs } from '../lib/view-as';
import {
  HUBS,
  HUB_ORDER,
  ALL_HUB,
  resolveHubView,
  uncuratedLeaves,
  getHubSel,
  setHubSel,
  type HubSel,
} from '../lib/hub-nav';
import { getPins, getRecents, subscribePins, togglePin, type PinnedOrg } from '../lib/pins';
import { useCachedFetch } from '../lib/use-cached-fetch';
import CommandPalette from './CommandPalette';
import type { RoleOrLocked } from '../lib/auth-types';

// RailShell — the app's navigation chassis (the role-based redesign).
//
// A persistent LEFT RAIL under a shared scoreboard ribbon. The rail is now
// driven by the selected HUB (Marketing / Product / CS / Sales / BDR / Exec):
// each hub shows a curated set of destinations for that role (see lib/hub-nav),
// with the top-bar "Hubs" switcher choosing the lens. The universal GTM spine
// still exists as the admin's "All destinations (advanced)" fallback and as the
// zero-regression view for any viewer whose grants match no persona.
//
// The hub lens NEVER changes real grants — anything a hub omits is still
// reachable via ⌘K, the "More views you can access" disclosure, and the Hubs
// menu. Routes + module gates are unchanged (canSeeNav carries the same rules).

export interface RailShellProps {
  email: string;
  role: RoleOrLocked;
  allowedModules: string[];
  onSignOut: () => void;
  /** Set while an admin is "viewing as" another user — drives the exit banner. */
  impersonating?: AuthMe['impersonating'];
  children: ReactNode;
}

export default function RailShell({
  email,
  role,
  allowedModules,
  onSignOut,
  impersonating,
  children,
}: RailShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = role === 'admin';
  const lockedOut = role === null;

  // Explicit section collapse overrides (legacy spine only); undefined =
  // default (auto-open when the section contains the active route).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // "What changed" badges — new activity per GTM section (legacy spine only).
  const { data: badges } = useCachedFetch<Record<string, number>>(lockedOut ? null : '/api/rail-badges');

  // The active hub lens. Admins persist + switch it; viewers are pinned to the
  // hub their grants resolve to (resolveHubView ignores this for non-admins).
  const [hubSel, setHubSelState] = useState<HubSel>(getHubSel);

  // ⌘K command palette.
  const [cmdkOpen, setCmdkOpen] = useState(false);
  // Mobile drawer — the ≤760px CSS hides `.rail-nav`; this is the replacement.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    if (lockedOut) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdkOpen((o) => !o);
      }
      if (e.key === 'Escape' && navOpen && !cmdkOpen) setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lockedOut, navOpen, cmdkOpen]);

  // Desktop (≥761px) never uses the drawer — drop it if the viewport crosses.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 761px)');
    const onChange = () => {
      if (mq.matches) setNavOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  // Pinned + recently-viewed accounts (personalization layer). Re-read whenever
  // a pin toggles or a recent is recorded (same-tab notify).
  const [pins, setPins] = useState<PinnedOrg[]>(getPins);
  const [recents, setRecents] = useState<PinnedOrg[]>(getRecents);
  useEffect(() => {
    const refresh = () => {
      setPins(getPins());
      setRecents(getRecents());
    };
    refresh();
    return subscribePins(refresh);
  }, []);

  async function signOut() {
    try {
      await apiAction('/api/auth/logout', {});
    } catch {
      // Already signed out / network blip — proceed with FE state reset.
    }
    onSignOut();
    navigate('/login');
  }

  const path = location.pathname;
  // A leaf is active for its exact route or any nested child (e.g. /orgs/:id
  // keeps the Accounts leaf lit). A leaf with a query string (e.g.
  // /paid-ads?view=decide) is active only when every one of its params matches
  // the current URL — so sibling leaves into the same page light one at a time.
  const leafActive = (to: string): boolean => {
    const q = to.indexOf('?');
    if (q !== -1) {
      if (path !== to.slice(0, q)) return false;
      const want = new URLSearchParams(to.slice(q + 1));
      const have = new URLSearchParams(location.search);
      for (const [k, v] of want) if (have.get(k) !== v) return false;
      return true;
    }
    return path === to || path.startsWith(to + '/');
  };
  // Overview (the admin north-star dashboard) lives at '/'; the role-resolved
  // Home lives at '/home'. Keep them as distinct rows so each lights its own.
  const overviewActive = path === '/';
  const homeActive = path === '/home';

  // Resolve the hub lens → what to render + how the switcher behaves.
  const hub = resolveHubView(role, allowedModules, hubSel);
  // Pinned/Recents are ACCOUNT shortcuts — show them only on account-centric
  // hubs (Sales/CS/BDR/Exec). Aggregate hubs (Marketing/Product) don't work
  // individual accounts, so the shortcuts are noise there. Legacy mode (admin
  // "All destinations" / a custom viewer) keeps them — it's the full spine.
  const showPersonalization = hub.mode === 'legacy' || (hub.hub?.accountCentric ?? false);

  // Pick a hub (admin control): swap the rail, keep the Home composition in
  // sync (via view-as), and jump to the hub's landing. "All (advanced)" is
  // nav-only — it doesn't touch view-as or navigate.
  function selectHub(k: HubSel) {
    setHubSel(k);
    setHubSelState(k);
    if (k === ALL_HUB) return;
    setViewAs(k);
    const to = HUBS[k].landing;
    navigate(to === '/home' ? `/home?as=${k}` : to);
  }

  // Legacy-spine data (admin "All" + custom viewers): visible sections + tail.
  const sections = lockedOut
    ? []
    : RAIL_SECTIONS.map((s) => ({ section: s, leaves: visibleLeaves(s, role, allowedModules) })).filter(
        (s) => s.leaves.length > 0,
      );
  const tail = lockedOut ? [] : RAIL_TAIL.filter((l) => canSeeNav(l, role, allowedModules));
  const defaultOpenJobs = personaOpenJobs(role, allowedModules);
  function sectionOpen(key: string, containsActive: boolean): boolean {
    if (collapsed[key] !== undefined) return !collapsed[key];
    return containsActive || defaultOpenJobs.includes(key);
  }

  // Curated-hub extras: leaves the viewer is granted but the hub omits — the
  // scoped viewer's safety net. Admins don't need it (they have the "All
  // (advanced)" option), so it's suppressed for them to avoid a noisy list.
  const moreViews =
    !isAdmin && hub.mode === 'hub' && hub.hub ? uncuratedLeaves(hub.hub, role, allowedModules) : [];

  const roleLabel = role ?? 'no access';
  const roleTooltip = `Your role: ${roleLabel}.`;

  // While impersonating, role/allowedModules are the TARGET's effective values,
  // so this label describes who the admin is currently emulating.
  const impersonationRoleLabel =
    role === 'admin' || role === 'viewer'
      ? personaLabel(matchPersona(role, allowedModules))
      : role ?? 'no access';

  const closeMenusOnPick = (e: ReactMouseEvent) => {
    const el = (e.target as HTMLElement).closest('a,button');
    if (el) (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open');
  };

  const closeNavOnLeaf = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) setNavOpen(false);
  };

  return (
    <div
      className={`rail-shell${impersonating ? ' rail-shell--impersonating' : ''}${navOpen ? ' rail-shell--nav-open' : ''}`}
    >
      <UpdateBanner />
      {impersonating && (
        <ImpersonationBanner
          actorEmail={impersonating.actorEmail}
          viewingEmail={impersonating.viewingEmail}
          roleLabel={impersonationRoleLabel}
        />
      )}
      <header className="rail-topbar">
        {!lockedOut && (
          <button
            type="button"
            className="rail-menu-btn"
            aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={navOpen}
            aria-controls="rail-primary-nav"
            onClick={() => setNavOpen((o) => !o)}
          >
            <span className="rail-menu-btn__ic" aria-hidden="true">
              {navOpen ? '✕' : '☰'}
            </span>
            <span className="rail-menu-btn__txt">Menu</span>
          </button>
        )}
        <div className="rail-topbar__brand">
          <span className="rail-brand-mark">Moxo</span> Funnel
        </div>
        {/* ⌘K — the role-agnostic fast path: scoped account search + jump-to.
            Also the escape hatch: it enumerates every granted destination,
            independent of the current hub. */}
        <button
          type="button"
          className="rail-search"
          onClick={() => setCmdkOpen(true)}
          title="Search orgs · deals · people · jump to a section (⌘K)"
          aria-label="Search orgs, deals, people, or jump to a section"
        >
          <span className="rail-search__label">Search orgs · deals · people · jump to…</span>
          <kbd className="rail-search__kbd" aria-hidden="true">⌘K</kbd>
        </button>
        <div className="rail-topbar__right">
          {!lockedOut &&
            (hub.canSwitch ? (
              <details className="rail-hubs" title="Switch hub — re-focuses the left nav (admin)">
                <summary className="rail-hubs__btn">
                  <span className="rail-hubs__lab">Hubs</span>
                  <span className="rail-hubs__cur">
                    <span className="rail-hubs__hex" aria-hidden="true">⬢</span>
                    <span className="rail-hubs__name">{hub.badgeLabel}</span>
                    <span className="rail-hubs__car" aria-hidden="true">▾</span>
                  </span>
                </summary>
                <div className="rail-hubs__panel" onClick={closeMenusOnPick}>
                  {HUB_ORDER.map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`rail-hubs__opt${hub.active === k ? ' rail-hubs__opt--on' : ''}`}
                      onClick={() => selectHub(k)}
                    >
                      <span className="rail-hubs__opt-t">{HUBS[k].label}</span>
                      <span className="rail-hubs__opt-s">{HUBS[k].blurb}</span>
                    </button>
                  ))}
                  <div className="rail-hubs__sep" />
                  <button
                    type="button"
                    className={`rail-hubs__opt rail-hubs__opt--adv${hub.active === ALL_HUB ? ' rail-hubs__opt--on' : ''}`}
                    onClick={() => selectHub(ALL_HUB)}
                  >
                    <span className="rail-hubs__opt-t">All destinations (advanced)</span>
                    <span className="rail-hubs__opt-s">The full un-curated spine — admin safety net.</span>
                  </button>
                </div>
              </details>
            ) : (
              <span className="rail-hubs__badge" title="Your hub — the left nav is focused for your role">
                <span className="rail-hubs__hex" aria-hidden="true">⬢</span>
                <span className="rail-hubs__name">{hub.badgeLabel}</span>
              </span>
            ))}
          <details className="account-menu">
            <summary
              className={`account-menu__chip role-badge role-badge--${role ?? 'locked'}`}
              title={`${email} · ${roleTooltip}`}
            >
              {email.slice(0, 1).toUpperCase()}
              <span className="account-menu__caret" aria-hidden="true">▾</span>
            </summary>
            <div className="account-menu__panel" onClick={closeMenusOnPick}>
              <div className="account-menu__id">
                <span className="who">{email}</span>
                <span className={`role-badge role-badge--${role ?? 'locked'}`} title={roleTooltip}>
                  {roleLabel}
                </span>
              </div>
              <div className="account-menu__row"><ThemeToggle /></div>
              {isAdmin && (
                <NavLink to="/settings" className="account-menu__item">
                  Settings
                </NavLink>
              )}
              <button className="account-menu__item account-menu__item--button" onClick={signOut}>
                Sign out
              </button>
            </div>
          </details>
        </div>
      </header>

      {/* Company scoreboard (Signups · Activated · Customers · ARR · Coverage)
          is an exec/finance alignment layer — admins only, in every hub. */}
      {!lockedOut && isAdmin && <ScoreboardRibbon />}

      <div className="rail-body">
        {navOpen && (
          <button
            type="button"
            className="rail-nav-backdrop"
            aria-label="Dismiss navigation overlay"
            onClick={() => setNavOpen(false)}
          />
        )}
        {!lockedOut && (
          <nav
            id="rail-primary-nav"
            className="rail-nav"
            aria-label="Primary"
            onClick={closeNavOnLeaf}
          >
            {/* Overview (admin north-star) + role-resolved Home are the anchor
                rows every hub carries; the hub curates everything below. */}
            {isAdmin && (
              <NavLink to="/" end className={`rail-row ${overviewActive ? 'rail-row--active' : ''}`}>
                <span className="rail-row__ic" aria-hidden="true">▸</span>
                Overview
                <span className="rail-row__badge">north-star</span>
              </NavLink>
            )}
            <NavLink to="/home" end className={`rail-row ${homeActive ? 'rail-row--active' : ''}`}>
              <span className="rail-row__ic" aria-hidden="true">▸</span>
              Home
              <span className="rail-row__badge">{isAdmin ? 'all' : 'you'}</span>
            </NavLink>

            {/* Pinned/Recents are the REAL admin's per-browser personalization;
                hide them while "viewing as" so the rep's view isn't polluted,
                and only on account-centric hubs (not Marketing/Product). */}
            {showPersonalization && !impersonating && pins.length > 0 && (
              <div className="rail-group">
                <div className="rail-grouplabel rail-grouplabel--pin">★ Pinned</div>
                {pins.map((o) => (
                  <NavLink
                    key={o.id}
                    to={`/orgs/${o.id}`}
                    className={({ isActive }) => `rail-leaf ${isActive ? 'rail-leaf--active' : ''}`}
                  >
                    <span className="rail-leaf__star" aria-hidden="true">★</span>
                    <span className="rail-leaf__txt">{o.name}</span>
                    <button
                      className="rail-leaf__unpin"
                      title="Unpin"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        togglePin(o);
                      }}
                    >
                      ×
                    </button>
                  </NavLink>
                ))}
              </div>
            )}

            {showPersonalization && !impersonating && recents.length > 0 && (
              <div className="rail-group">
                <div className="rail-grouplabel">Recents</div>
                {recents.map((o) => (
                  <NavLink
                    key={o.id}
                    to={`/orgs/${o.id}`}
                    className={({ isActive }) => `rail-leaf ${isActive ? 'rail-leaf--active' : ''}`}
                  >
                    <span className="rail-leaf__dot" aria-hidden="true">·</span>
                    <span className="rail-leaf__txt">{o.name}</span>
                  </NavLink>
                ))}
              </div>
            )}

            <div className="rail-divider" />

            {/* HUB MODE — the curated per-role rail. */}
            {hub.mode === 'hub' && hub.hub && (
              <>
                {hub.hub.groups
                  .map((g) => ({ g, leaves: g.leaves.filter((l) => canSeeNav(l, role, allowedModules)) }))
                  .filter((x) => x.leaves.length > 0)
                  .map(({ g, leaves }, gi) => (
                    <div className="rail-group" key={g.label ?? `g${gi}`}>
                      {g.label && <div className="rail-grouplabel">{g.label}</div>}
                      {leaves.map((l) => (
                        <NavLink
                          key={l.to}
                          to={l.to}
                          end={l.end}
                          className={() =>
                            `rail-leaf ${leafActive(l.to) ? 'rail-leaf--active' : ''}`
                          }
                        >
                          <span className="rail-leaf__dot" aria-hidden="true">·</span>
                          {l.label}
                          {l.to === '/pulse' && <span className="rail-leaf__tag">ungated</span>}
                        </NavLink>
                      ))}
                    </div>
                  ))}

                {moreViews.length > 0 && (
                  <details className="rail-more">
                    <summary className="rail-more__summary">
                      More views you can access ({moreViews.length})
                    </summary>
                    {moreViews.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        className={() =>
                          `rail-leaf rail-more__row ${leafActive(l.to) ? 'rail-leaf--active' : ''}`
                        }
                      >
                        <span className="rail-leaf__dot" aria-hidden="true">·</span>
                        {l.label}
                      </NavLink>
                    ))}
                  </details>
                )}

                <p className="rail-note">
                  {hub.canSwitch
                    ? `Curated for the ${hub.hub.label} hub — switch hubs above, or reach anything via ⌘K.`
                    : `Curated for your ${hub.hub.label} hub. Anything you can access stays reachable via ⌘K${moreViews.length ? ' or “More views” above' : ''} — nothing is walled off.`}
                </p>
              </>
            )}

            {/* LEGACY MODE — the full GTM spine (admin "All", or custom grants). */}
            {hub.mode === 'legacy' && (
              <>
                {sections.map(({ section, leaves }) => {
                  const containsActive = leaves.some((l) => leafActive(l.to));
                  const open = sectionOpen(section.key, containsActive);
                  return (
                    <div className="rail-group" key={section.key}>
                      <button
                        type="button"
                        className={`rail-grouphdr ${open ? '' : 'rail-grouphdr--collapsed'}`}
                        onClick={() => setCollapsed((c) => ({ ...c, [section.key]: open }))}
                      >
                        <span className="rail-grouphdr__chev" aria-hidden="true">▼</span>
                        {section.label}
                        {badges && badges[section.key] > 0 && (
                          <span
                            className="rail-grouphdr__badge"
                            title={`${badges[section.key]} new in the last 7 days`}
                          >
                            {badges[section.key] > 99 ? '99+' : badges[section.key]}
                          </span>
                        )}
                        <span className="rail-grouphdr__ord">{section.ord}/7</span>
                      </button>
                      {open &&
                        leaves.map((l) => (
                          <NavLink
                            key={l.to}
                            to={l.to}
                            className={() =>
                              `rail-leaf ${leafActive(l.to) ? 'rail-leaf--active' : ''}`
                            }
                          >
                            <span className="rail-leaf__dot" aria-hidden="true">·</span>
                            {l.label}
                          </NavLink>
                        ))}
                    </div>
                  );
                })}

                <div className="rail-divider" />
                <div className="rail-grouplabel">Business · cross-cutting</div>
                {tail.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `rail-leaf ${isActive || leafActive(l.to) ? 'rail-leaf--active' : ''}`
                    }
                  >
                    <span className="rail-leaf__dot" aria-hidden="true">·</span>
                    {l.label}
                    {l.to === '/pulse' && <span className="rail-leaf__tag">ungated</span>}
                  </NavLink>
                ))}
                <p className="rail-note">
                  Every destination, ungated by hub — the admin safety net. Reachable any time from the
                  Hubs menu.
                </p>
              </>
            )}
          </nav>
        )}
        <main className="rail-content">
          <div className="rail-content__inner">{children}</div>
        </main>
      </div>
      {cmdkOpen && !lockedOut && (
        <CommandPalette role={role} allowedModules={allowedModules} onClose={() => setCmdkOpen(false)} />
      )}
    </div>
  );
}
